import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ShieldAlert,
  Users,
  DollarSign,
  TrendingDown,
  TrendingUp,
  Building2,
  LogOut,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertCircle,
  Filter,
  Search,
  CheckCircle2,
  XCircle,
  Activity,
  MessageCircle,
  Send,
  ArrowLeft,
} from "lucide-react";

const SORT_OPTIONS = [
  { id: "cost_usd_desc", label: "Mayor costo IA" },
  { id: "margin_asc", label: "Margen más bajo (riesgo)" },
  { id: "calls_desc", label: "Más llamadas IA" },
  { id: "recent", label: "Actividad reciente" },
];

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(n || 0);

const fmtInt = (n) =>
  new Intl.NumberFormat("es-MX").format(n || 0);

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [report, setReport] = useState(null);
  const [waConfig, setWaConfig] = useState(null);
  const [waTesting, setWaTesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("cost_usd_desc");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | paid | unpaid | inactive | negative
  const [actionId, setActionId] = useState(null);

  useEffect(() => {
    // Guard: must be superadmin
    if (user && user.role !== "superadmin") {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [k, r, wa] = await Promise.all([
        api.get("/superadmin/dashboard"),
        api.get(`/superadmin/cost-per-user?sort=${sort}`),
        api.get("/superadmin/whatsapp-config").catch(() => ({ data: null })),
      ]);
      setKpi(k.data);
      setReport(r.data);
      setWaConfig(wa.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  const testWhatsApp = async () => {
    setWaTesting(true);
    try {
      const { data } = await api.post("/superadmin/whatsapp-test");
      if (data.ok) {
        toast.success("WhatsApp enviado. Revisa tu celular.");
      } else {
        toast.error("CallMeBot no respondió 200. Revisa la API key y que tu número esté activado.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error de red");
    } finally {
      setWaTesting(false);
    }
  };

  useEffect(() => {
    load();
  }, [sort]);

  const filteredItems = useMemo(() => {
    if (!report?.items) return [];
    const q = query.trim().toLowerCase();
    return report.items.filter((it) => {
      if (q && !(`${it.email} ${it.name} ${it.university || ""}`.toLowerCase().includes(q))) return false;
      if (filter === "paid" && !it.has_paid) return false;
      if (filter === "unpaid" && it.has_paid) return false;
      if (filter === "inactive" && it.is_active) return false;
      if (filter === "negative" && it.margin_usd >= 0) return false;
      return true;
    });
  }, [report, query, filter]);

  const onToggle = async (it) => {
    const goingInactive = it.is_active;
    if (goingInactive) {
      const ok = window.confirm(
        `¿Suspender la cuenta de ${it.email}?\n\nCosto IA acumulado: ${fmtUSD(it.cost_usd)}\nMargen actual: ${fmtUSD(it.margin_usd)}\n\nSe le notificará por correo y no podrá entrar hasta reactivarlo (o que pague otros $69 USD).`
      );
      if (!ok) return;
    }
    setActionId(it.user_id);
    try {
      await api.post(`/superadmin/users/${it.user_id}/toggle-active`, {
        is_active: !it.is_active,
      });
      toast.success(goingInactive ? "Cuenta suspendida y usuario notificado" : "Cuenta reactivada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cambiando estado");
    } finally {
      setActionId(null);
    }
  };

  const onLogout = async () => {
    await logout?.();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="superadmin-dashboard-page">
      <header className="sticky top-0 z-30 border-b border-rule bg-ink/95 text-paper backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-8 w-8 rounded-full bg-copper/20 text-copper">
              <ShieldAlert size={15} />
            </span>
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">Super Admin</p>
              <p className="text-[13px] text-paper">{user?.name || "Operaciones"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="superadmin-back-home"
              className="inline-flex items-center gap-1.5 text-[12px] text-paper/70 hover:text-paper px-3 py-1.5 rounded-full border border-paper/15 hover:border-paper/40 transition-colors"
            >
              <ArrowLeft size={12} /> Volver al inicio
            </Link>
            <button
              data-testid="superadmin-refresh"
              onClick={load}
              className="inline-flex items-center gap-1.5 text-[12px] text-paper/70 hover:text-paper px-3 py-1.5 rounded-full border border-paper/15 hover:border-paper/40 transition-colors"
            >
              <RefreshCw size={12} /> Refrescar
            </button>
            <Link to="/admin" className="text-[12px] text-paper/70 hover:text-paper px-3 py-1.5 rounded-full border border-paper/15 hover:border-paper/40 transition-colors">
              Admin clásico
            </Link>
            <button
              data-testid="superadmin-logout"
              onClick={onLogout}
              className="h-8 w-8 grid place-items-center rounded-full border border-paper/15 text-paper/70 hover:text-paper hover:border-paper/40"
              title="Cerrar sesión"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* KPI Cards */}
        <section data-testid="superadmin-kpi-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={Users}
            label="Estudiantes"
            value={fmtInt(kpi?.users?.total)}
            sub={`${fmtInt(kpi?.users?.active)} activos · ${fmtInt(kpi?.users?.inactive)} inactivos`}
            color="forest"
            loading={loading}
          />
          <KpiCard
            icon={DollarSign}
            label="Ingresos totales"
            value={fmtUSD(kpi?.revenue?.total_usd)}
            sub={`Stripe ${fmtUSD(kpi?.revenue?.stripe_usd)} · BBVA ${fmtUSD(kpi?.revenue?.bank_usd)}`}
            color="copper"
            loading={loading}
          />
          <KpiCard
            icon={Activity}
            label="Costo IA (acumulado)"
            value={fmtUSD(kpi?.ai_cost?.total_usd)}
            sub={`${fmtInt(kpi?.ai_cost?.total_calls)} llamadas LLM`}
            color="sand"
            loading={loading}
          />
          <KpiCard
            icon={(kpi?.margin?.usd || 0) >= 0 ? TrendingUp : TrendingDown}
            label="Margen neto"
            value={fmtUSD(kpi?.margin?.usd)}
            sub={`${(kpi?.margin?.pct || 0).toFixed(1)}% utilidad bruta`}
            color={(kpi?.margin?.usd || 0) >= 0 ? "forest" : "rose"}
            loading={loading}
          />
        </section>

        {/* WhatsApp alert status */}
        <section className="mt-4" data-testid="whatsapp-alert-card">
          <div className={`rounded-2xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
            waConfig?.configured
              ? "border-forest/30 bg-forest/5"
              : "border-copper/40 bg-copper/5"
          }`}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className={`grid place-items-center h-10 w-10 rounded-full shrink-0 ${
                waConfig?.configured ? "bg-forest/15 text-forest" : "bg-copper/20 text-copper"
              }`}>
                <MessageCircle size={17} />
              </span>
              <div className="min-w-0">
                <p className="text-[10.5px] tracking-[0.22em] uppercase font-medium text-ink-soft">
                  Alerta WhatsApp automática
                </p>
                <p className="mt-0.5 text-[13.5px] text-ink leading-tight">
                  {waConfig?.configured ? (
                    <>
                      Conectada · Te avisaremos al{" "}
                      <span className="font-medium text-forest">${waConfig.threshold_usd.toFixed(0)} USD</span>{" "}
                      de costo IA por alumno · Destino{" "}
                      <span className="font-mono">{waConfig.phone}</span>
                    </>
                  ) : (
                    <>
                      Pendiente de activar · Umbral{" "}
                      <span className="font-medium">${waConfig?.threshold_usd?.toFixed(0) || 25} USD</span>{" "}
                      por alumno · Destino{" "}
                      <span className="font-mono">{waConfig?.phone || "+523342802102"}</span>
                    </>
                  )}
                </p>
                {!waConfig?.configured && (
                  <p className="mt-1.5 text-[11.5px] text-ink-soft leading-relaxed">
                    Activa CallMeBot: agrega <span className="font-mono">+34 644 51 95 23</span> a tus contactos, envíale por WhatsApp{" "}
                    <span className="italic">&ldquo;I allow callmebot to send me messages&rdquo;</span>, recibe tu API key y dásela a tu desarrollador para configurar <span className="font-mono">CALLMEBOT_APIKEY</span>.
                  </p>
                )}
              </div>
            </div>
            <button
              data-testid="whatsapp-test-btn"
              onClick={testWhatsApp}
              disabled={waTesting || !waConfig?.configured}
              className={`shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-[12.5px] font-medium transition-all ${
                waConfig?.configured
                  ? "bg-forest text-paper hover:bg-forest-dark shadow-sm"
                  : "bg-ink-soft/15 text-ink-soft/60 cursor-not-allowed"
              }`}
            >
              {waTesting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {waTesting ? "Enviando…" : "Probar WhatsApp"}
            </button>
          </div>
        </section>

        {/* Module: Bank transfers shortcut */}
        <section className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <ModuleCard
            href="/admin/bank-transfers"
            icon={Building2}
            title="Transferencias BBVA"
            badge={kpi?.revenue?.bank_pending_count ? `${kpi.revenue.bank_pending_count} pendientes` : "Sin pendientes"}
            badgeColor={kpi?.revenue?.bank_pending_count ? "copper" : "forest"}
            desc="Aprueba o rechaza las transferencias que reporten los estudiantes contra tu cuenta BBVA."
          />
          <ModuleCard
            href="/admin"
            icon={Users}
            title="Usuarios (Admin clásico)"
            badge="Lista completa"
            badgeColor="forest"
            desc="Vista clásica con búsqueda y promoción/democión de roles administrativos."
          />
          <ModuleCard
            icon={AlertCircle}
            title="Riesgo (margen negativo)"
            badge={`${report?.totals?.negative_margin_users || 0} cuenta(s)`}
            badgeColor={(report?.totals?.negative_margin_users || 0) > 0 ? "rose" : "forest"}
            desc="Estudiantes cuyo consumo de IA ya supera lo que pagaron. Revísalos abajo y suspéndelos si conviene."
          />
        </section>

        {/* COSTO POR USUARIO — main report */}
        <section className="mt-10" data-testid="cost-per-user-report">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10.5px] tracking-[0.22em] uppercase text-copper font-medium">Reporte estratégico</p>
              <h2 className="mt-2 font-serif text-3xl text-ink">Costo por Usuario</h2>
              <p className="mt-2 text-[13px] text-ink-soft max-w-2xl">
                Mide la relación <strong>costo/beneficio</strong> de cada estudiante. El margen es{" "}
                <span className="text-forest font-medium">ingreso (USD)</span> − costo IA acumulado. Margen negativo en rojo: candidato a suspensión.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  data-testid="cost-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar correo, nombre…"
                  className="pl-9 pr-4 py-2 rounded-full border border-rule bg-white text-[12.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition w-56"
                />
              </div>
              <select
                data-testid="cost-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="py-2 px-3 rounded-full border border-rule bg-white text-[12.5px] cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Filter chips */}
          <div className="mt-4 flex items-center gap-2 flex-wrap" role="tablist">
            {[
              { id: "all", label: `Todos (${report?.items?.length || 0})` },
              { id: "paid", label: `Han pagado (${report?.totals?.paid_users || 0})` },
              { id: "unpaid", label: "Sin pagar" },
              { id: "inactive", label: `Suspendidos (${(report?.items || []).filter(i => !i.is_active).length})` },
              { id: "negative", label: `Margen negativo (${report?.totals?.negative_margin_users || 0})` },
            ].map((f) => (
              <button
                key={f.id}
                data-testid={`cost-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`inline-flex items-center gap-1.5 text-[11.5px] px-3 py-1 rounded-full border transition-all ${
                  filter === f.id
                    ? "bg-forest text-paper border-forest"
                    : "bg-paper-2 text-ink-soft border-rule hover:border-forest/30"
                }`}
              >
                <Filter size={10} /> {f.label}
              </button>
            ))}
          </div>

          {/* Totals strip */}
          {report?.totals && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cost-totals">
              <TotalChip label="Costo total IA" value={fmtUSD(report.totals.total_cost_usd)} tone="copper" />
              <TotalChip label="Ingresos cobrados" value={fmtUSD(report.totals.total_revenue_usd)} tone="forest" />
              <TotalChip
                label="Margen neto"
                value={fmtUSD(report.totals.total_margin_usd)}
                tone={report.totals.total_margin_usd >= 0 ? "forest" : "rose"}
              />
              <TotalChip
                label="Riesgo (margen<0)"
                value={`${report.totals.negative_margin_users} cuenta(s)`}
                tone={report.totals.negative_margin_users > 0 ? "rose" : "forest"}
              />
            </div>
          )}

          {/* Table */}
          <div className="mt-5 rounded-2xl border border-rule bg-white overflow-hidden">
            {loading ? (
              <div className="px-6 py-20 grid place-items-center text-ink-soft">
                <Loader2 size={22} className="animate-spin" />
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="px-6 py-16 text-center text-ink-soft text-[13px]">
                No hay usuarios que coincidan con tu filtro.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px] min-w-[920px]">
                  <thead className="bg-paper-2 text-ink-soft text-[10.5px] tracking-[0.16em] uppercase font-medium">
                    <tr>
                      <th className="text-left px-4 py-3">Estudiante</th>
                      <th className="text-right px-4 py-3">Llamadas IA</th>
                      <th className="text-right px-4 py-3">Tokens (in/out)</th>
                      <th className="text-right px-4 py-3">Costo IA</th>
                      <th className="text-right px-4 py-3">Ingreso</th>
                      <th className="text-right px-4 py-3">Margen</th>
                      <th className="text-center px-4 py-3">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it) => {
                      const negative = it.margin_usd < 0;
                      return (
                        <tr key={it.user_id} className="border-t border-rule/60 hover:bg-paper-2/40" data-testid={`cost-row-${it.user_id}`}>
                          <td className="px-4 py-3 align-top">
                            <p className="text-ink font-medium leading-tight">{it.name || "Sin nombre"}</p>
                            <p className="text-[11px] text-ink-soft mt-0.5 break-all">{it.email}</p>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              {it.has_paid && <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-forest/10 text-forest px-2 py-0.5"><CheckCircle2 size={9} /> Pagó</span>}
                              {!it.is_active && <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-rose-100 text-rose-700 px-2 py-0.5"><XCircle size={9} /> Suspendido</span>}
                              {it.country && <span className="text-[10px] text-ink-soft/80">· {it.country}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right align-top tabular-nums">{fmtInt(it.calls)}</td>
                          <td className="px-4 py-3 text-right align-top tabular-nums text-[11px] text-ink-soft">
                            {fmtInt(it.input_tokens)}/{fmtInt(it.output_tokens)}
                          </td>
                          <td className="px-4 py-3 text-right align-top tabular-nums" data-testid={`cost-usd-${it.user_id}`}>
                            <span className="text-ink font-medium">{fmtUSD(it.cost_usd)}</span>
                          </td>
                          <td className="px-4 py-3 text-right align-top tabular-nums text-ink-soft">
                            {fmtUSD(it.revenue_usd)}
                          </td>
                          <td className={`px-4 py-3 text-right align-top tabular-nums font-medium ${negative ? "text-rose-700" : "text-forest"}`} data-testid={`margin-usd-${it.user_id}`}>
                            {fmtUSD(it.margin_usd)}
                          </td>
                          <td className="px-4 py-3 text-center align-top">
                            <button
                              data-testid={`toggle-active-${it.user_id}`}
                              onClick={() => onToggle(it)}
                              disabled={actionId === it.user_id}
                              title={it.is_active ? "Suspender cuenta (ACTIVO)" : "Reactivar cuenta (INACTIVO)"}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all disabled:opacity-60 ${
                                it.is_active
                                  ? "bg-forest/10 text-forest hover:bg-forest/15"
                                  : "bg-rose-100 text-rose-700 hover:bg-rose-200"
                              }`}
                            >
                              {actionId === it.user_id ? (
                                <Loader2 size={11} className="animate-spin" />
                              ) : it.is_active ? (
                                <ToggleRight size={13} />
                              ) : (
                                <ToggleLeft size={13} />
                              )}
                              {it.is_active ? "ACTIVO" : "INACTIVO"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="mt-3 text-[11px] text-ink-soft/80 text-right">
            Algoritmo de costo: tokens estimados × precio oficial Anthropic/OpenAI (Claude $3/$15 · GPT-4o $2.5/$10 por M tokens).
          </p>
        </section>
      </main>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color = "forest", loading }) {
  const map = {
    forest: "bg-forest/10 text-forest",
    copper: "bg-copper/10 text-copper",
    sand: "bg-[#E0C58A]/30 text-[#8b6722]",
    rose: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="rounded-2xl border border-rule bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-ink-soft font-medium">{label}</p>
          <p className="mt-1.5 font-serif text-2xl text-ink leading-tight">
            {loading ? <span className="text-ink-soft">…</span> : value}
          </p>
        </div>
        <span className={`grid place-items-center h-9 w-9 rounded-full ${map[color]}`}>
          <Icon size={15} />
        </span>
      </div>
      <p className="mt-2 text-[11px] text-ink-soft leading-tight">{sub}</p>
    </div>
  );
}

function ModuleCard({ href, icon: Icon, title, badge, badgeColor = "forest", desc }) {
  const Tag = href ? Link : "div";
  const badgeMap = {
    forest: "bg-forest/10 text-forest",
    copper: "bg-copper/15 text-copper",
    rose: "bg-rose-100 text-rose-700",
  };
  return (
    <Tag
      to={href}
      data-testid={href ? `module-${href.replaceAll("/", "-")}` : `module-${title.toLowerCase().replaceAll(" ", "-")}`}
      className="group rounded-2xl border border-rule bg-white p-5 hover:border-forest/30 hover:shadow-md transition-all flex flex-col gap-2 cursor-pointer"
    >
      <div className="flex items-center justify-between">
        <span className="grid place-items-center h-9 w-9 rounded-full bg-paper-2 text-ink">
          <Icon size={15} />
        </span>
        <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-0.5 rounded-full font-medium ${badgeMap[badgeColor]}`}>{badge}</span>
      </div>
      <p className="font-serif text-lg text-ink mt-2">{title}</p>
      <p className="text-[12px] text-ink-soft leading-relaxed">{desc}</p>
    </Tag>
  );
}

function TotalChip({ label, value, tone = "forest" }) {
  const map = {
    forest: "border-forest/30 bg-forest/5",
    copper: "border-copper/30 bg-copper/5",
    rose: "border-rose-300 bg-rose-50 text-rose-700",
  };
  return (
    <div className={`rounded-xl border px-4 py-2.5 ${map[tone]}`}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-ink-soft font-medium">{label}</p>
      <p className="text-[15.5px] text-ink font-medium tabular-nums">{value}</p>
    </div>
  );
}
